/**
 * TipTap schema pieces for the document template visual editor.
 *
 * The editor never sees raw Scriban — scribanHtmlTransform.ts rewrites it to
 * `data-scriban-expr` spans and `data-scriban-for` attributes first. This file makes those
 * markers first-class citizens of the ProseMirror schema so `editor.getHTML()` round-trips them:
 *   - ScribanExpr: atomic inline node rendered as a locked chip (edited via popover only)
 *   - ScribanFor: global attribute preserving the loop marker on repeatable elements
 *   - PreserveClass: global attribute keeping `class` (the default template relies on it)
 */
import { Extension, Node, generateHTML, generateJSON } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import { ScribanExprChipView } from './ScribanExprChipView';
import { extractScribanInventory } from './scribanHtmlTransform';

/** `{{ expr }}` as an atomic inline chip. The expression is opaque — never edited as free text. */
export const ScribanExpr = Node.create({
  name: 'scribanExpr',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      expr: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-scriban-expr') ?? '',
        renderHTML: (attributes) => ({ 'data-scriban-expr': String(attributes.expr ?? '') }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-scriban-expr]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ScribanExprChipView);
  },
});

/** Node types a `{{ for }}` loop marker may sit on (matches what the transformer can target). */
const LOOP_CAPABLE_TYPES = [
  'table',
  'tableRow',
  'tableCell',
  'tableHeader',
  'paragraph',
  'heading',
  'listItem',
  'bulletList',
  'orderedList',
];

/** Preserves `data-scriban-for` loop markers through parse/serialize. */
export const ScribanFor = Extension.create({
  name: 'scribanFor',
  addGlobalAttributes() {
    return [
      {
        types: LOOP_CAPABLE_TYPES,
        attributes: {
          'data-scriban-for': {
            default: null,
            parseHTML: (element) => element.getAttribute('data-scriban-for'),
            renderHTML: (attributes) =>
              attributes['data-scriban-for'] ? { 'data-scriban-for': attributes['data-scriban-for'] } : {},
          },
        },
      },
    ];
  },
});

/** Keeps `class` attributes (e.g. the default template's `class="totals"`) through editing. */
export const PreserveClass = Extension.create({
  name: 'preserveClass',
  addGlobalAttributes() {
    return [
      {
        types: [...LOOP_CAPABLE_TYPES, 'blockquote', 'codeBlock', 'horizontalRule'],
        attributes: {
          class: {
            default: null,
            parseHTML: (element) => element.getAttribute('class'),
            renderHTML: (attributes) => (attributes.class ? { class: attributes.class } : {}),
          },
        },
      },
    ];
  },
});

export function buildTemplateEditorExtensions() {
  return [
    StarterKit,
    Table.configure({ resizable: false }),
    TableRow,
    TableHeader.extend({ content: 'inline*' }),
    // Inline-only cells so `<td>text</td>` round-trips without gaining a `<p>` wrapper
    // (which would change PDF rendering through paragraph margins). Cells with block
    // content are rare in print templates and stay editable in the code view.
    TableCell.extend({ content: 'inline*' }),
    ScribanExpr,
    ScribanFor,
    PreserveClass,
  ];
}

/**
 * Tags the schema above can represent without silent loss. Anything else (div layouts, images,
 * inline-styled spans, colgroup, …) blocks the visual toggle — the code view stays authoritative.
 */
const SUPPORTED_TAGS = new Set([
  'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
  'blockquote', 'pre', 'code',
  'br', 'hr',
  'b', 'strong', 'i', 'em', 'u', 's', 'del', 'strike', 'a',
  'span',
]);

/**
 * Removes serialization noise TipTap's table extension adds (default colspan/rowspan="1",
 * generated colgroup, min-width inline style) so visual edits don't churn the template source.
 * Source templates never legitimately contain these: colgroup is blocked by the fidelity
 * whitelist and the table schema has no style attribute, so anything matched here is editor noise.
 */
export function stripEditorTableNoise(html: string): string {
  return html
    .replace(/<colgroup[\s\S]*?<\/colgroup>/g, '')
    .replace(/ (?:colspan|rowspan)="1"/g, '')
    .replace(/(<table[^>]*?) style="min-width:[^"]*"/g, '$1');
}

export type FidelityResult = { ok: true } | { ok: false; reasons: string[] };

const visibleText = (html: string) =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Data-loss safety net run before entering the visual editor: parses the marker HTML through
 * the actual TipTap schema and verifies that (a) only representable tags are present, (b) every
 * Scriban marker survives, and (c) no visible text is dropped. Requires a DOM.
 */
export function checkVisualFidelity(editableBodyHtml: string): FidelityResult {
  const reasons: string[] = [];

  const tagRegex = /<([a-zA-Z][a-zA-Z0-9-]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/g;
  const unsupported = new Set<string>();
  for (let m = tagRegex.exec(editableBodyHtml); m; m = tagRegex.exec(editableBodyHtml)) {
    const tag = m[1].toLowerCase();
    if (!SUPPORTED_TAGS.has(tag)) unsupported.add(tag);
    else if (tag === 'span' && !m[2].includes('data-scriban-expr')) unsupported.add('span (without a placeholder)');
  }
  for (const tag of unsupported) {
    reasons.push(`The template contains <${tag}> elements the visual editor cannot represent`);
  }
  if (reasons.length > 0) return { ok: false, reasons };

  const extensions = buildTemplateEditorExtensions();
  let roundTripped: string;
  try {
    roundTripped = generateHTML(generateJSON(editableBodyHtml, extensions), extensions);
  } catch {
    return { ok: false, reasons: ['The template could not be loaded into the visual editor'] };
  }

  const before = extractScribanInventory(editableBodyHtml);
  const after = extractScribanInventory(roundTripped);
  if (JSON.stringify(before) !== JSON.stringify(after)) {
    reasons.push('Some placeholders or loops would be lost in the visual editor');
  }
  if (visibleText(editableBodyHtml) !== visibleText(roundTripped)) {
    reasons.push('Some text content would be changed or removed by the visual editor');
  }
  return reasons.length > 0 ? { ok: false, reasons } : { ok: true };
}
